import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { orders } from "@/api/orders";
import { useActiveAccount } from "@/components/ActiveAccount";
import { useToast } from "@/components/Toast";
import { useWsSubscribe } from "@/ws/WebSocketProvider";
import { Badge, Button, EmptyState, Skeleton } from "@/components/ui";

/**
 * Resting limit orders for the active trading account. One-click cancel
 * removes from both the in-memory L2 matcher and the persisted
 * `internal_limit_orders` row. Bulk Cancel-All sweeps every open limit.
 */
export function OpenOrdersPane() {
  const { active } = useActiveAccount();
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["limit-orders", active?.id],
    queryFn: () => orders.listLimits(active?.id),
    enabled: !!active,
    refetchInterval: 5_000,
  });
  useWsSubscribe("internal_fill", () => {
    qc.invalidateQueries({ queryKey: ["limit-orders"] });
  });
  useWsSubscribe("fill", () => {
    qc.invalidateQueries({ queryKey: ["limit-orders"] });
  });

  const cancel = useMutation({
    mutationFn: (id: string) => orders.cancelLimit(id),
    onSuccess: () => {
      toast.push("Limit canceled", "warn");
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message : String(e), "error"),
  });

  const cancelAll = useMutation({
    mutationFn: async () => {
      if (!data) return null;
      const results = await Promise.allSettled(
        data.map((o) => orders.cancelLimit(o.id)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      return { total: data.length, failed };
    },
    onSuccess: (r) => {
      if (!r) return;
      const ok = r.total - r.failed;
      toast.push(
        r.failed === 0
          ? `Canceled ${ok}/${r.total} limit orders`
          : `Canceled ${ok}/${r.total} (${r.failed} failed)`,
        r.failed === 0 ? "warn" : "error",
      );
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
    },
  });

  if (!active) {
    return <EmptyState title="No active account" description="Select a trading account to view your open orders." />;
  }
  if (isLoading) {
    return (
      <div className="space-y-1.5 p-2">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <EmptyState title="No open orders" description="Submit a LIMIT order from the ticket — it'll rest here until filled." />
    );
  }
  return (
    <div className="flex h-full flex-col text-2xs">
      <div className="flex items-center justify-between border-b border-term-border bg-term-bg px-2 py-1">
        <span className="uppercase tracking-wider text-term-mute">{data.length} open</span>
        <Button
          variant="danger"
          onClick={() => {
            if (confirm(`Cancel all ${data.length} limit orders?`)) cancelAll.mutate();
          }}
          disabled={cancelAll.isPending}
        >
          Cancel all
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-term-bg text-term-mute">
            <tr>
              <th className="px-2 py-1 text-left font-medium uppercase tracking-wider">Sym</th>
              <th className="px-2 py-1 text-left font-medium uppercase tracking-wider">Side</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Qty</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Price</th>
              <th className="px-2 py-1 text-left font-medium uppercase tracking-wider">Status</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {data.map((o) => (
              <tr key={o.id} className="border-t border-term-border hover:bg-term-elev">
                <td className="px-2 py-1 font-medium text-term-fg">{o.symbol}</td>
                <td className={`px-2 py-1 ${o.side === "BUY" ? "up" : "down"}`}>{o.side}</td>
                <td className="px-2 py-1 text-right tnum text-term-dim">
                  {o.qty_remaining}
                  {o.qty_remaining !== o.qty_original && (
                    <span className="ml-1 text-term-mute">/ {o.qty_original}</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right tnum text-term-fg">{o.price}</td>
                <td className="px-2 py-1">
                  <Badge tone={o.status === "PARTIAL" ? "warn" : "good"}>{o.status}</Badge>
                </td>
                <td className="px-2 py-1 text-right">
                  <Button
                    variant="ghost"
                    onClick={() => cancel.mutate(o.id)}
                    disabled={cancel.isPending}
                  >
                    Cancel
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
